// Wrapper face-api untuk absensi anti-fraud.
//
// DESAIN: PWA hanya menghitung DESCRIPTOR wajah (128 float) + cek liveness
// best-effort secara lokal. Server-lah yang mencocokkan descriptor vs wajah
// terdaftar dan memutuskan faceScore/faceVerified. Client TIDAK pernah sekadar
// klaim "verified" — itu sebabnya kita kirim descriptor mentah, bukan boolean.
//
// Model TIDAK dibundel (≈6MB). Di-load runtime dari CDN jsdelivr saat pertama
// dibutuhkan. Semua fungsi defensif: kalau model gagal / wajah tidak terdeteksi,
// caller (HomePage) tetap bisa clock-in dengan graceful fallback.

// Type-only import: TIDAK menarik runtime ke bundle utama.
import type * as FaceApi from '@vladmandic/face-api';

// Weights resmi @vladmandic/face-api di-host di jsdelivr.
const MODEL_URL = 'https://cdn.jsdelivr.net/npm/@vladmandic/face-api/model';

// Lazy-load library face-api (≈1MB) hanya saat fitur wajah dipakai → code-split
// keluar dari bundle utama supaya beranda tetap ringan.
let faceapi: typeof FaceApi | null = null;
let libPromise: Promise<typeof FaceApi> | null = null;
function importFaceApi(): Promise<typeof FaceApi> {
    if (faceapi) return Promise.resolve(faceapi);
    if (!libPromise) {
        libPromise = import('@vladmandic/face-api').then((m) => {
            faceapi = m;
            return m;
        });
    }
    return libPromise;
}

let loadPromise: Promise<boolean> | null = null;
let modelsReady = false;
let detectorOptions: FaceApi.TinyFaceDetectorOptions | null = null;

/** Apakah model sudah siap dipakai (tanpa memicu load). */
export function modelsLoaded(): boolean {
    return modelsReady;
}

/**
 * Load library + model sekali. Idempotent + race-safe. Mengembalikan true jika
 * siap, false jika gagal (caller harus fallback ke clock-in tanpa wajah).
 */
export function loadModels(): Promise<boolean> {
    if (modelsReady) return Promise.resolve(true);
    if (loadPromise) return loadPromise;

    loadPromise = (async () => {
        try {
            const fa = await importFaceApi();
            await Promise.all([
                fa.nets.tinyFaceDetector.loadFromUri(MODEL_URL),
                fa.nets.faceLandmark68Net.loadFromUri(MODEL_URL),
                fa.nets.faceRecognitionNet.loadFromUri(MODEL_URL),
            ]);
            detectorOptions = new fa.TinyFaceDetectorOptions({ inputSize: 320, scoreThreshold: 0.5 });
            modelsReady = true;
            return true;
        } catch (err) {
            // Reset supaya percobaan berikutnya bisa load lagi (mis. koneksi pulih).
            loadPromise = null;
            modelsReady = false;
            return false;
        }
    })();

    return loadPromise;
}

export interface FaceDetection {
    /** Descriptor 128-float untuk dikirim ke server. */
    descriptor: number[];
    /** Confidence deteksi wajah (0..1). */
    detectionScore: number;
    /** Landmark mata kiri+kanan untuk perhitungan liveness antar-frame. */
    landmarks: FaceApi.FaceLandmarks68;
}

type Source = HTMLVideoElement | HTMLCanvasElement | HTMLImageElement;

/**
 * Deteksi 1 wajah + hitung descriptor & landmark dari sumber video/canvas/image.
 * Mengembalikan null jika model belum siap atau tidak ada wajah terdeteksi.
 */
export async function detectFace(source: Source): Promise<FaceDetection | null> {
    if (!modelsReady) {
        const ok = await loadModels();
        if (!ok) return null;
    }
    if (!faceapi || !detectorOptions) return null;
    try {
        const result = await faceapi
            .detectSingleFace(source as any, detectorOptions)
            .withFaceLandmarks()
            .withFaceDescriptor();
        if (!result) return null;
        return {
            descriptor: Array.from(result.descriptor),
            detectionScore: result.detection.score,
            landmarks: result.landmarks,
        };
    } catch {
        return null;
    }
}

/**
 * Convenience: hanya descriptor (untuk enrollment). null kalau gagal.
 */
export async function getDescriptor(source: Source): Promise<number[] | null> {
    const det = await detectFace(source);
    return det ? det.descriptor : null;
}

// ─── Liveness (best-effort, client-side) ──────────────────────────────
// Strategi: bandingkan 2 frame. Hitung Eye Aspect Ratio (EAR) tiap frame +
// pergeseran posisi landmark. Liveness lulus kalau ada KEDIPAN (EAR turun
// signifikan lalu naik) ATAU GERAKAN kepala yang cukup. Foto diam = gagal.

function eyeAspectRatio(eye: FaceApi.Point[]): number {
    // eye = 6 titik. EAR = (|p2-p6| + |p3-p5|) / (2*|p1-p4|)
    if (eye.length < 6) return 0;
    const d = (a: FaceApi.Point, b: FaceApi.Point) => Math.hypot(a.x - b.x, a.y - b.y);
    const vert = d(eye[1], eye[5]) + d(eye[2], eye[4]);
    const horiz = 2 * d(eye[0], eye[3]);
    return horiz === 0 ? 0 : vert / horiz;
}

function avgEar(lm: FaceApi.FaceLandmarks68): number {
    return (eyeAspectRatio(lm.getLeftEye()) + eyeAspectRatio(lm.getRightEye())) / 2;
}

/** Pusat wajah (rata-rata semua landmark) untuk deteksi pergeseran. */
function faceCenter(lm: FaceApi.FaceLandmarks68): { x: number; y: number; scale: number } {
    const pts = lm.positions;
    let sx = 0;
    let sy = 0;
    for (const p of pts) {
        sx += p.x;
        sy += p.y;
    }
    const cx = sx / pts.length;
    const cy = sy / pts.length;
    // skala wajah (jarak antar mata) untuk normalisasi gerakan
    const le = lm.getLeftEye();
    const re = lm.getRightEye();
    const lc = le.reduce((a, p) => ({ x: a.x + p.x / le.length, y: a.y + p.y / le.length }), { x: 0, y: 0 });
    const rc = re.reduce((a, p) => ({ x: a.x + p.x / re.length, y: a.y + p.y / re.length }), { x: 0, y: 0 });
    const scale = Math.hypot(lc.x - rc.x, lc.y - rc.y) || 1;
    return { x: cx, y: cy, scale };
}

export interface LivenessResult {
    passed: boolean;
    blinkDetected: boolean;
    movementDetected: boolean;
}

/**
 * Cek liveness dari 2 deteksi berurutan (frame awal & frame setelah jeda).
 * Caller bertanggung jawab memberi jeda (~600-1200ms) + minta user kedip/gerak.
 */
export function checkLiveness(first: FaceDetection, second: FaceDetection): LivenessResult {
    const ear1 = avgEar(first.landmarks);
    const ear2 = avgEar(second.landmarks);
    // Kedipan: EAR turun di bawah threshold relatif terhadap baseline.
    const minEar = Math.min(ear1, ear2);
    const maxEar = Math.max(ear1, ear2);
    const blinkDetected = maxEar > 0 && minEar < maxEar * 0.78 && minEar < 0.22;

    const c1 = faceCenter(first.landmarks);
    const c2 = faceCenter(second.landmarks);
    const scale = (c1.scale + c2.scale) / 2;
    const shift = Math.hypot(c1.x - c2.x, c1.y - c2.y) / scale;
    // Gerakan dianggap nyata kalau pergeseran > ~8% lebar mata-ke-mata.
    const movementDetected = shift > 0.08;

    return {
        passed: blinkDetected || movementDetected,
        blinkDetected,
        movementDetected,
    };
}
